import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackAddToCart, trackRemoveFromCart, cartItemToAnalytics } from "@/lib/analytics";

export interface CartProduct {
  id: string;
  title: string;
  price: number;
  regularPrice?: number | null;
  cover_url: string | null;
  type: "book" | "lesson" | "zemelapis";
  author?: string | null;
  /** For zemelapis only: which product/tier. Ignored otherwise. */
  zemelapisTier?: "single" | "gift_pair" | "gift_single" | "karjera" | "karjera_gift";
  /** For zemelapis gift tiers only: optional personalisation fields. */
  giftSenderName?: string;
  giftRecipientName?: string;
  giftRecipientEmail?: string;
  giftMessage?: string;
}

interface CartItem {
  product: CartProduct;
  quantity: number;
  /** Epoch ms when the item was added. Items older than 30 days are dropped on load. */
  addedAt?: number;
}

interface DiscountInfo {
  code: string;
  type: string;
  value: number;
  appliesTo: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (product: CartProduct) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  total: number;
  discountedTotal: number;
  count: number;
  discount: DiscountInfo | null;
  discountLoading: boolean;
  discountError: string | null;
  applyDiscount: (code: string) => Promise<void>;
  removeDiscount: () => void;
  cartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  /** Single digital product bought straight away — bypasses the stored cart. */
  expressItem: CartProduct | null;
  /** Opens checkout with only this product; the stored cart is left untouched. */
  openExpressCheckout: (product: CartProduct) => void;
  /** Closes checkout and clears any express item. */
  closeCheckout: () => void;
}

const CART_STORAGE_KEY = "ld_cart_items";
const CART_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function loadCartFromStorage(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    // Drop anything sitting in the cart for more than 30 days.
    return (parsed as CartItem[]).filter(
      (i) => i?.product && (typeof i.addedAt !== "number" || now - i.addedAt < CART_MAX_AGE_MS)
    );
  } catch {
    // Corrupted storage — ignore
  }
  return [];
}

function saveCartToStorage(items: CartItem[]) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage full or unavailable
  }
}


const CartContext = createContext<CartContextType>({
  items: [],
  addItem: () => {},
  removeItem: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  total: 0,
  discountedTotal: 0,
  count: 0,
  discount: null,
  discountLoading: false,
  discountError: null,
  applyDiscount: async () => {},
  removeDiscount: () => {},
  cartOpen: false,
  setCartOpen: () => {},
  expressItem: null,
  openExpressCheckout: () => {},
  closeCheckout: () => {},
});

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => loadCartFromStorage());
  const [discount, setDiscount] = useState<DiscountInfo | null>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [cartOpen, setCartOpenState] = useState(false);
  const [expressItem, setExpressItem] = useState<CartProduct | null>(null);

  /** Opening the regular cart always leaves express mode. */
  const setCartOpen = (open: boolean) => {
    if (open) setExpressItem(null);
    setCartOpenState(open);
  };

  const openExpressCheckout = (product: CartProduct) => {
    trackAddToCart(cartItemToAnalytics(product, 1));
    setExpressItem(product);
    setCartOpenState(true);
  };

  const closeCheckout = () => {
    setCartOpenState(false);
    setExpressItem(null);
  };


  // Persist cart to localStorage whenever items change
  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);

  const addItem = (product: CartProduct) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) return prev;
      trackAddToCart(cartItemToAnalytics(product, 1));
      return [...prev, { product, quantity: 1, addedAt: Date.now() }];
    });
  };

  const removeItem = (productId: string) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product.id === productId);
      if (existing) trackRemoveFromCart(cartItemToAnalytics(existing.product, existing.quantity));
      return prev.filter((i) => i.product.id !== productId);
    });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) return;
    setItems((prev) =>
      prev.map((i) => (i.product.id === productId ? { ...i, quantity } : i))
    );
  };

  const clearCart = () => {
    setItems([]);
    setDiscount(null);
    try { localStorage.removeItem(CART_STORAGE_KEY); } catch { /* noop */ }
  };

  const applyDiscount = async (code: string) => {
    setDiscountLoading(true);
    setDiscountError(null);
    try {
      const { data, error } = await supabase
        .rpc("validate_discount_code", { discount_code: code.trim() });

      const record = Array.isArray(data) ? data[0] : data;

      if (error || !record) {
        setDiscountError("Nuolaidos kodas nerastas.");
        setDiscount(null);
        return;
      }

      const scopeTotal = expressItem ? expressItem.price : total;
      if (record.min_order_amount && scopeTotal < record.min_order_amount) {
        setDiscountError(`Min. užsakymo suma: €${record.min_order_amount.toFixed(2)}`);
        return;
      }

      const appliesTo: string = record.applies_to || 'all';
      // In express mode the checkout holds only the express product — validate against that.
      const scope = expressItem ? [expressItem] : items.map(i => i.product);
      const hasBooks = scope.some(p => p.type === 'book');
      const hasLessons = scope.some(p => p.type === 'lesson');

      if (appliesTo === 'books' && !hasBooks) {
        setDiscountError("Šis nuolaidos kodas taikomas tik knygoms.");
        return;
      }
      if (appliesTo === 'lessons' && !hasLessons) {
        setDiscountError("Šis nuolaidos kodas taikomas tik pamokoms.");
        return;
      }

      setDiscount({ code: record.code, type: record.type, value: record.value, appliesTo });
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountError(null);
  };

  const total = items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

  const discountedTotal = (() => {
    if (!discount) return total;
    // Calculate which items the discount applies to
    // Items already on sale (price < regularPrice) are NOT eligible for additional discount codes
    const eligibleTotal = items.reduce((sum, i) => {
      const alreadyOnSale = i.product.regularPrice != null && i.product.price < i.product.regularPrice;
      if (alreadyOnSale) return sum; // skip already-discounted items
      if (discount.appliesTo === 'all' || 
          (discount.appliesTo === 'books' && i.product.type === 'book') ||
          (discount.appliesTo === 'lessons' && i.product.type === 'lesson')) {
        return sum + i.product.price * i.quantity;
      }
      return sum;
    }, 0);
    const nonEligibleTotal = total - eligibleTotal;
    
    const discountedEligible = discount.type === "percentage"
      ? Math.max(0, eligibleTotal * (1 - discount.value / 100))
      : Math.max(0, eligibleTotal - discount.value);
    
    return nonEligibleTotal + discountedEligible;
  })();

  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, total, discountedTotal, count, discount, discountLoading, discountError, applyDiscount, removeDiscount, cartOpen, setCartOpen, expressItem, openExpressCheckout, closeCheckout }}>
      {children}
    </CartContext.Provider>
  );
};
